// ============================================================================
// GREEN CIRCLE — CENTRAL SITE CONFIGURATION
// Edit brand, contact, legal, navigation, plans, industries, FAQs and case
// studies here. Do NOT invent prices, statistics, testimonials or accreditations.
// ============================================================================

export const brand = {
  name: "Green Circle Group",
  fullName: "Green Circle Group",
  // Legal entity (confirmed): Green Circle Group Australia, ABN 16 338 218 680.
  owner: "Green Circle Group Australia",
  legalName: "Green Circle Group Australia",
  abn: "16 338 218 680",
  address: "", // to be confirmed
  domain: "greencirclegroup.com.au",
  siteUrl: "https://greencirclegroup.com.au",
  email: "hello@greencirclegroup.com.au",
  logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_visible-local-jobs/artifacts/p17a5014_Green_Circle_Group_Logo_V3.png",
  tagline: "Digital Growth for Australian Trades",
  promise: "Great trades deserve to be found.",
  bookingUrl: "", // external booking link, initially empty -> falls back to /contact
};

export const cta = {
  primary: { label: "Book a Free Growth Assessment", to: "/contact" },
  secondary: { label: "Compare Growth Plans", to: "/plans" },
};

export const nav = [
  { label: "For Tradies", to: "/for" },
  { label: "Plans", to: "/plans" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Industries", to: "/industries" },
  { label: "About", to: "/about" },
];

export const socials = {
  // initially empty — footer hides icons that have no url
  linkedin: "",
  facebook: "",
  instagram: "",
};

export const trustChips = [
  "Built for Australian Trades",
  "Subscription-Based Support",
  "Strategy and Execution",
  "One Connected Growth System",
];

export const growthStages = [
  {
    step: "01",
    title: "Get Found",
    desc: "Improve your local presence so the right customers can discover and trust your business.",
  },
  {
    step: "02",
    title: "Get Leads",
    desc: "Use the right channels, offers and campaigns to create relevant enquiries in your service area.",
  },
  {
    step: "03",
    title: "Get Booked",
    desc: "Capture, respond to and follow up opportunities more consistently.",
  },
  {
    step: "04",
    title: "Keep Improving",
    desc: "Track what is producing real opportunities and continually improve the system.",
  },
];

export const problems = [
  {
    title: "Your business is difficult to find",
    desc: "Your Google presence, website and social profiles are not showing customers the real quality of your work.",
  },
  {
    title: "Enquiries are inconsistent",
    desc: "You rely heavily on referrals, shared leads or irregular marketing activity.",
  },
  {
    title: "Good leads are being lost",
    desc: "Missed calls, delayed replies and limited follow-up allow ready customers to move on.",
  },
  {
    title: "Growth depends on you",
    desc: "Marketing and business development only happen when you personally find the time.",
  },
];

export const processSteps = [
  {
    step: "01",
    title: "Assess",
    desc: "We understand your business, services, market, current marketing and growth constraints.",
  },
  {
    step: "02",
    title: "Set Up",
    desc: "We strengthen the foundations, configure tracking and build the agreed campaigns and workflows.",
  },
  {
    step: "03",
    title: "Launch",
    desc: "Your selected growth activities go live across the right channels and systems.",
  },
  {
    step: "04",
    title: "Manage",
    desc: "Green Circle Group manages the recurring activity included in your subscription.",
  },
  {
    step: "05",
    title: "Improve",
    desc: "We review performance, identify bottlenecks and refine the system over time.",
  },
];

export const whyPoints = [
  {
    title: "Built for busy owners",
    desc: "The service is designed for people who cannot spend every evening managing marketing, leads and systems.",
  },
  {
    title: "Commercially focused",
    desc: "We care about relevant enquiries, booked opportunities and business capability—not attention for its own sake.",
  },
  {
    title: "Connected from end to end",
    desc: "Visibility, campaigns, lead capture, follow-up, reviews and reporting work as parts of one system.",
  },
  {
    title: "Adapted to your market",
    desc: "The strategy reflects your trade, location, capacity and growth objectives.",
  },
  {
    title: "Clear and accountable",
    desc: "You should understand what is being managed, what is working and where improvement is required.",
  },
];

export const changes = [
  "From an incomplete Google presence to a credible local profile.",
  "From inconsistent marketing to a managed monthly system.",
  "From scattered enquiries to one visible pipeline.",
  "From slow follow-up to structured response workflows.",
  "From forgotten past customers to compliant reactivation opportunities.",
  "From uncertain marketing activity to clearer commercial tracking.",
];

// ---------------------------------------------------------------------------
// PLANS — single source of truth (rendered on Home preview + Plans page)
// ---------------------------------------------------------------------------
export const plans = [
  {
    id: "get-found",
    name: "Get Found",
    subtitle: "Local Visibility Foundation",
    tagline: "Build your local presence",
    recommended: false,
    description:
      "For businesses that need stronger visibility, credibility and consistency across the places customers are already looking.",
    inclusions: [
      "Local business and competitor assessment",
      "Google Business Profile optimisation and ongoing management",
      "Local digital-presence review",
      "Review-generation system",
      "Review monitoring and response support",
      "Branded social credibility content",
      "Ongoing local-presence improvements",
      "Monthly visibility and action summary",
    ],
    notes: [],
    ctaLabel: "Explore Get Found",
  },
  {
    id: "get-leads",
    name: "Get Leads",
    subtitle: "Managed Lead Engine",
    tagline: "Build a managed enquiry engine",
    recommended: true,
    description:
      "Everything in Get Found, plus coordinated campaigns, creative, landing pages and lead tracking designed to produce more relevant opportunities.",
    inclusions: [
      "Everything in Get Found",
      "Paid campaign strategy",
      "Google and/or Meta campaign management according to the trade and market",
      "Advertisement and creative production",
      "Service-specific landing page",
      "Call and form tracking",
      "Lead capture and pipeline",
      "Immediate enquiry acknowledgement",
      "Retargeting",
      "Ongoing campaign optimisation",
      "Commercial performance review",
    ],
    notes: [
      "Advertising media spend is separate. Channel recommendations depend on your trade, service area and growth objectives.",
    ],
    ctaLabel: "Explore Get Leads",
  },
  {
    id: "cruise-control",
    name: "Cruise Control",
    subtitle: "Enquiry-to-Booking Support",
    tagline: "Turn more enquiries into booked opportunities",
    recommended: false,
    description:
      "Everything in Get Found and Get Leads, plus active lead-response, follow-up and booking support to keep opportunities moving while you are busy.",
    inclusions: [
      "Everything in Get Found and Get Leads",
      "Lead-response and qualification support",
      "Quote, site-visit or job appointment booking",
      "Missed-call text-back capability",
      "Structured enquiry follow-up",
      "Quote follow-up support",
      "Old-lead and eligible customer-database reactivation",
      "Offer and sales-script optimisation",
      "CRM and pipeline management",
      "Review and referral workflows",
      "Quarterly growth planning",
      "Potential trade-category exclusivity in an agreed service territory",
    ],
    notes: [
      "Database campaigns are subject to consent, privacy and communication requirements.",
      "Trade-category exclusivity may be available within an agreed service territory, subject to location and package availability.",
    ],
    ctaLabel: "Explore Cruise Control",
  },
];

export const comparisonRows = [
  { capability: "Initial business and competitor assessment", found: true, leads: true, cruise: true },
  { capability: "Google Business Profile optimisation", found: true, leads: true, cruise: true },
  { capability: "Ongoing local-presence management", found: true, leads: true, cruise: true },
  { capability: "Review-generation system", found: true, leads: true, cruise: true },
  { capability: "Social credibility content", found: true, leads: true, cruise: true },
  { capability: "Paid campaign management", found: false, leads: true, cruise: true },
  { capability: "Campaign creative production", found: false, leads: true, cruise: true },
  { capability: "Service-specific landing page", found: false, leads: true, cruise: true },
  { capability: "Call and form tracking", found: false, leads: true, cruise: true },
  { capability: "Lead capture and pipeline", found: false, leads: true, cruise: true },
  { capability: "Retargeting", found: false, leads: true, cruise: true },
  { capability: "Lead-response support", found: false, leads: false, cruise: true },
  { capability: "Enquiry qualification", found: false, leads: false, cruise: true },
  { capability: "Appointment or quote booking support", found: false, leads: false, cruise: true },
  { capability: "Missed-call and follow-up workflows", found: false, leads: false, cruise: true },
  { capability: "CRM and pipeline management", found: false, leads: false, cruise: true },
  { capability: "Eligible database-reactivation support", found: false, leads: false, cruise: true },
  { capability: "Quarterly growth planning", found: false, leads: false, cruise: true },
  { capability: "Potential territory/category exclusivity", found: false, leads: false, cruise: "Subject to availability" },
];

export const planNotes = [
  "Subscription prices and onboarding fees are provided following the Growth Assessment.",
  "Advertising media spend is separate unless expressly stated in a proposal.",
  "Final deliverables, service areas, usage allowances and response scope are confirmed in the client proposal.",
  "Results vary according to the market, offer, competition, budget, capacity and client response.",
  "Green Circle Group does not guarantee rankings, lead volume, sales or revenue.",
  "Database communication is subject to consent, privacy, unsubscribe and applicable communication requirements.",
];

// ---------------------------------------------------------------------------
// SOLUTIONS
// ---------------------------------------------------------------------------
export const solutions = [
  {
    title: "Local Visibility and Reputation",
    desc: "Help customers find and trust the business through Google Business Profile optimisation, local presence management, listings, reviews, useful updates and consistent brand presentation.",
  },
  {
    title: "Websites and Landing Pages",
    desc: "Create clear, mobile-first experiences that explain services, establish credibility and make it simple for customers to call, enquire or request a quote.",
  },
  {
    title: "Digital Advertising and Lead Generation",
    desc: "Plan and manage search, social and retargeting campaigns around the client's services, locations, capacity and commercial priorities.",
  },
  {
    title: "Lead Capture and Pipeline",
    desc: "Bring calls, forms and campaign enquiries into a structured view so opportunities can be tracked and acted upon.",
  },
  {
    title: "Follow-Up and Booking Workflows",
    desc: "Support faster response, missed-call handling, lead nurturing, quote follow-up and appointment booking according to the selected package.",
  },
  {
    title: "CRM, Automation and Practical AI",
    desc: "Introduce useful technology that reduces manual work, improves consistency and gives the owner better visibility—without adding unnecessary complexity.",
  },
  {
    title: "Reviews, Referrals and Reactivation",
    desc: "Create compliant systems for requesting reviews, encouraging referrals and identifying appropriate opportunities within previous enquiries or customer relationships.",
  },
  {
    title: "Ongoing Growth Management",
    desc: "Provide recurring management, optimisation and growth planning through a subscription rather than leaving the owner with another strategy document to implement alone.",
  },
];

// ---------------------------------------------------------------------------
// VALUES (About)
// ---------------------------------------------------------------------------
export const values = [
  { title: "Clarity", desc: "Make marketing, technology and business-development decisions easier to understand." },
  { title: "Progress", desc: "Prioritise work that moves the business forward rather than creating activity for its own sake." },
  { title: "Connection", desc: "Bring visibility, campaigns, customer response and systems together." },
  { title: "Accountability", desc: "Be clear about what is being managed, what is working and what requires improvement." },
  { title: "Practicality", desc: "Recommend solutions that suit the client's business, capacity and real working environment." },
];

// ---------------------------------------------------------------------------
// CASE STUDIES — intentionally empty. Do not invent. Carousel hides when empty.
// ---------------------------------------------------------------------------
export const caseStudies = [];

// ---------------------------------------------------------------------------
// ANALYTICS — env-var placeholders. Tags only load when IDs are present.
// ---------------------------------------------------------------------------
export const analytics = {
  gtmId: process.env.REACT_APP_GTM_ID || "",
  ga4Id: process.env.REACT_APP_GA4_ID || "",
  metaPixelId: process.env.REACT_APP_META_PIXEL_ID || "",
};
